/**
 * Razorpay Checkout loader + opener.
 *
 * Usage:
 *   const { ok, paymentId, orderId, signature } = await openRazorpayCheckout({
 *     orderId, amount, currency, keyId, user, name, description, theme,
 *   });
 */
const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadCheckoutScript() {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") return reject(new Error("no-window"));
        if (window.Razorpay) return resolve(true);
        if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
            const s = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
            s.addEventListener("load", () => resolve(true));
            s.addEventListener("error", () => reject(new Error("checkout.js failed to load")));
            return;
        }
        const s = document.createElement("script");
        s.src = SCRIPT_SRC;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error("checkout.js failed to load"));
        document.head.appendChild(s);
    });
}

/**
 * Opens the Razorpay checkout modal and resolves with the payment response,
 * or rejects when the user dismisses or payment fails.
 */
export async function openRazorpayCheckout({
    keyId, orderId, amount, currency = "INR",
    user, name = "Drive or Kick", description = "Booking payment",
    theme = "#00FF66",
}) {
    await loadCheckoutScript();
    if (!window.Razorpay) throw new Error("Razorpay SDK not loaded");

    return new Promise((resolve, reject) => {
        // Track whether the success handler fired so we don't show a
        // "Payment cancelled" error from a late modal-dismiss event after
        // a UPI success (Razorpay sometimes fires both for UPI Intent flows).
        let handlerFired = false;
        const rzp = new window.Razorpay({
            key: keyId,
            amount,
            currency,
            name,
            description,
            order_id: orderId,
            // Explicit: keep the success callback in-page (no full-page redirect).
            // This is the recommended config for SPA UPI handling.
            redirect: false,
            retry: { enabled: true, max_count: 1 },
            prefill: {
                name: user?.name || "",
                email: user?.email || "",
                contact: user?.phone || "",
            },
            notes: {
                booking_for: user?.email || "",
            },
            theme: { color: theme },
            handler: (resp) => {
                handlerFired = true;
                resolve({
                    ok: true,
                    paymentId: resp.razorpay_payment_id,
                    orderId: resp.razorpay_order_id,
                    signature: resp.razorpay_signature,
                });
            },
            modal: {
                ondismiss: () => {
                    // If the handler already fired (UPI success → modal closes),
                    // ignore this dismiss to avoid a false "cancelled" toast.
                    if (handlerFired) return;
                    reject(new Error("Payment cancelled"));
                },
                escape: true,
                backdropclose: false,
            },
        });
        rzp.on("payment.failed", (resp) => {
            if (handlerFired) return;  // success already handled
            reject(new Error(resp.error?.description || "Payment failed"));
        });
        rzp.open();
    });
}
